import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getSeoEntry } from "../seo-data";

export function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

type ButtonLinkProps = {
  to: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  showArrow?: boolean;
  className?: string;
};

export function ButtonLink({ to, children, variant = "primary", showArrow = false, className }: ButtonLinkProps) {
  const classes = cn("button", `button--${variant}`, className);
  const content = <>{children}{showArrow && <ArrowRight aria-hidden="true" size={17} />}</>;
  if (to.startsWith("http") || to.startsWith("tel:")) {
    return <a className={classes} href={to}>{content}</a>;
  }
  return <Link className={classes} to={to}>{content}</Link>;
}

export function Button({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={cn("button button--primary", className)} {...props} />;
}

export function Badge({ children, className, ...props }: HTMLAttributes<HTMLSpanElement> & { children: ReactNode }) {
  return <span className={cn("badge", className)} {...props}>{children}</span>;
}

export function Section({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return <section className={cn("section", className)} id={id}><div className="container">{children}</div></section>;
}

export function SectionHeading({ eyebrow, title, text, align = "left" }: { eyebrow?: string; title: string; text?: string; align?: "left" | "center" }) {
  return (
    <div className={cn("section-heading", align === "center" && "section-heading--center")}>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

export function PageHero({ eyebrow, title, intro, children }: { eyebrow: string; title: string; intro: string; children?: ReactNode }) {
  const { pathname } = useLocation();
  const breadcrumbs = getSeoEntry(pathname).breadcrumbs;
  return (
    <section className="page-hero">
      <div className="container page-hero__inner">
        <div className="page-hero__copy">
          {breadcrumbs && breadcrumbs.length > 1 && (
            <nav className="breadcrumbs" aria-label="Broodkruimelnavigatie">
              <ol>{breadcrumbs.map((crumb, index) => <li key={crumb.path}>{index === breadcrumbs.length - 1 ? <span aria-current="page">{crumb.name}</span> : <Link to={crumb.path}>{crumb.name}</Link>}</li>)}</ol>
            </nav>
          )}
          <span className="eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{intro}</p>
          {children && <div className="page-hero__actions">{children}</div>}
        </div>
        <div className="page-hero__signal" aria-hidden="true">
          <span /><span /><span /><span />
        </div>
      </div>
    </section>
  );
}

export function CheckList({ items, columns = false }: { items: string[]; columns?: boolean }) {
  return (
    <ul className={cn("check-list", columns && "check-list--columns")}>
      {items.map((item) => <li key={item}><span className="check-list__mark" aria-hidden="true">✓</span><span>{item}</span></li>)}
    </ul>
  );
}

export function Callout({ title, text, action = "Plan een kennismaking", to = "/contact", secondaryLink }: { title: string; text: string; action?: string; to?: string; secondaryLink?: { href: string; label: string } }) {
  return (
    <Section className="section--callout">
      <div className="callout">
        <div>
          <span className="eyebrow eyebrow--light">Samen verder</span>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>
        <div className="callout__actions">
          <ButtonLink to={to} variant="secondary" showArrow>{action}</ButtonLink>
          <a className="callout__phone" href="tel:+31353690150">Of bel 035 369 01 50</a>
          {secondaryLink && <a className="callout__secondary-link" href={secondaryLink.href} target="_blank" rel="noreferrer">{secondaryLink.label}</a>}
        </div>
      </div>
    </Section>
  );
}
